import React, { useState, useEffect } from 'react';
import { sanitizeString } from 'utils/xss';
import { Layout, Menu, Tooltip, Popover, Input, Button, message, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  TocIcon,
  CloseIcon,
  EyeIcon2,
  EyeoffIcon2,
  EditIcon3,
  DownloadIcon,
  MoreIcon,
  TextCrossIcon,
  TextIcon2,
} from 'components/Icons';
import { getSectionTitle } from 'helpers/proposal';
import useWindowDimensions from 'hooks/useWindowDimensions';

import './ContentTable.scss';

/**
 *
 * @param {Object} configText - The text configuration object.
 * @param {boolean} collapse - Whether the content table is collapsed.
 * @param {function} setCollapse - Function to set the collapse state.
 * @param {Object} proposal - The proposal object.
 * @param {Object} user - The user object.
 * @param {function} scrollToSection - Function to scroll to a section.
 * @param {string} contentTableClassName - The class name for the content table.
 * @param {function} downloadPdf - Function to download the PDF.
 * @returns {JSX.Element} - The content table component.
 */
const ContentTable = ({
  configText,
  collapse,
  setCollapse,
  proposal,
  user,
  scrollToSection,
  contentTableClassName,
  downloadPdf,
  downloadingPDF,
  handleDownloadCancel,
  isHebrew,
  config,
  saveProposal,
  path,
  clientWixPreview,
  showTocMenu,
  showDownloadButton,
}) => {
  const { width } = useWindowDimensions();

  const normalizeBoolean = (value) => value === true || value === 'true';
  const isHeaderHidden = normalizeBoolean(proposal?.draft?.header?.hidecover);
  const allowPreviewDownload = user?.profile?.allowPreviewDownload !== false;
  const configHeadline = proposal?.draft?.header?.headline || configText('Headline', true);

  const sectionOrder = proposal?.draft?.sectionorder?.filter(
    (section) => Object.keys(proposal?.draft[section] || {})?.length
  );

  const fullWidthImgArray = proposal?.draft?.sectionorder?.filter(
    (section) => Object.keys(proposal?.draft[section]?.image || {})?.length
  );

  const [headlineVisible, toggleHeadlineEdit] = useState(false);
  const [headlineName, setHeadlineName] = useState(configHeadline);
  const [headlineNameError, setHeadlineNameError] = useState('');

  const [fullImageVisible, toggleFullImageEdit] = useState(false);
  const [fullImageName, setFullImageName] = useState('');
  const [fullImageNameError, setFullImageNameError] = useState('');
  const [eachFullImage, setEachFullImage] = useState([]);
  const [tocExclude, setTocExclude] = useState([]);
  const [proposalExclude, setProposalExclude] = useState([]);

  useEffect(() => {
    if (!proposal?.draft?.header?.headline && configHeadline) {
      setHeadlineName(configHeadline);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configHeadline]);

  useEffect(() => {
    // get excluded from TOC list
    if (proposal?.draft?.tocExclude) {
      setTocExclude(proposal.draft.tocExclude);
    }
  }, [proposal?.draft?.tocExclude]);

  useEffect(() => {
    // keep previous behavior as fallback for older proposals
    setProposalExclude(proposal?.draft?.proposalExclude || []);
  }, [proposal?.draft?.proposalExclude, proposal?.draft?.tocExclude]);

  const arrCurrentNames = eachFullImage;

  /**
   * Get the current name of a section.
   * @param {string} sid - The section id.
   * @param {string} newName - The new name.
   * @returns {string} - The current name.
   */
  const getCurrentName = (sid, newName) => {
    if (arrCurrentNames !== null) {
      Object.keys(arrCurrentNames).forEach(function (key) {
        arrCurrentNames[key] = newName;
      });
    } else {
      arrCurrentNames[sid] = newName;
      setEachFullImage(arrCurrentNames);
    }
    return sid;
  };

  /**
   * Update the text of a section.
   * @param {Object} actionType - The action type.
   * @param {string} value - The value.
   * @param {string} sectionId - The section id.
   */
  const updateTOCText = ({ actionType, value, sectionId }) => {
    if (actionType === 'update_headline') {
      saveProposal({ 'draft.header.headline': value }, ({ data }) => {
        if (data) {
          message.success(`Headline successfully updated`);
        }
      });
    } else if (actionType === 'update_fullimage') {
      saveProposal(
        {
          [`draft.${sectionId}.imageName`]: value,
        },
        ({ data }) => {
          if (data) {
            message.success(`Image name successfully updated`);
          }
        }
      );
    } else if (actionType === 'update_sectionName') {
      saveProposal(
        {
          [`draft.${sectionId}.sectionName`]: value,
        },
        ({ data }) => {
          if (data) {
            message.success(`Section name successfully updated`);
          }
        }
      );
    }
  };

  /**
   * Update the exclude list of a section.
   * @param {Event} e - The event.
   * @param {string} key - The key.
   * @param {boolean} exclude - Whether the section is excluded.
   */
  const updateTOCExclude = (e, key, exclude) => {
    e.stopPropagation();

    let value = [...tocExclude];

    if (!exclude) {
      // if not excluded, remove from exclude list
      value = value.filter((item) => item !== key);
    } else if (!value.includes(key)) {
      // if excluded, add to exclude list
      value.push(key);
    }

    setTocExclude(value);

    saveProposal({ 'draft.tocExclude': value });
  };

  /**
   * Update the exclude list of a section.
   * @param {Event} e - The event.
   * @param {string} key - The key.
   * @param {boolean} exclude - Whether the section is excluded.
   */
  const updateProposalExclude = (e, key, exclude) => {
    e.stopPropagation();

    if (key === 'header') {
      saveProposal({ 'draft.header.hidecover': exclude });
      return;
    }

    if (key === 'signature') {
      saveProposal({ disableSig: exclude });
      return;
    }

    let value = [...proposalExclude];

    if (!exclude) {
      value = value.filter((item) => item !== key);
    } else if (!value.includes(key)) {
      value.push(key);
    }

    setProposalExclude(value);
    saveProposal({ 'draft.proposalExclude': value });
  };

  /**
   * Save the text of a section by pressing enter.
   * @param {Event} e - The event.
   * @param {string} actionType - The action type.
   * @param {string} s - The section id.
   */
  const saveByEnter = (e, actionType, s) => {
    const x = e.which || e.keyCode;
    if (e.key === 'Enter' || x === 13) {
      const isNotCombinedKey = !(e.ctrlKey || e.altKey || e.shiftKey);
      if (isNotCombinedKey) {
        if (actionType === 'update_headline') {
          updateTOCText({
            actionType: actionType,
            value: headlineName,
            sectionId: s,
          });
          toggleHeadlineEdit(false);
        } else if (actionType === 'update_fullimage') {
          updateTOCText({
            actionType: actionType,
            value: fullImageName,
            sectionId: s,
          });
          toggleFullImageEdit(false);
        } else if (actionType === 'update_sectionName') {
          updateTOCText({
            actionType: actionType,
            value: fullImageName,
            sectionId: s,
          });
          toggleFullImageEdit(false);
        }
      }
    }
  };

  /**
   * Get the class name for the content table.
   * @returns {string} - The class name.
   */
  const cdClassName = () => {
    const url = window.location.pathname;
    if (url.includes('/cd/') || clientWixPreview) {
      return 'client-preview';
    } else {
      return;
    }
  };

  /**
   * Handle the scroll to a section.
   * @param {Object} config - The configuration object.
   */
  const handleScrollToSection = (config) => {
    if (width < 768) {
      setCollapse(true);
    }
    scrollToSection(config);
  };

  const isEditor = path === '/d/:pid';

  /**
   * Get whether a section is hidden from the proposal.
   * @param {string} sectionId - The section id.
   * @returns {boolean} - Whether the section is hidden from the proposal.
   */
  const getIsHiddenFromProposal = (sectionId) => {
    if (sectionId === 'header') {
      return isHeaderHidden;
    }

    if (sectionId === 'signature') {
      return !!proposal?.disableSig;
    }

    return proposalExclude.includes(sectionId);
  };

  return (
    <Layout.Sider
      className={`content-table-side-menu ${cdClassName()} 
      ${contentTableClassName} 
      ${collapse ? 'ant-layout-sider-is-collapsed' : 'ant-layout-sider-is-not-collapsed'}`}>
      {!collapse && <div className="toc-closing-mask" onClick={() => setCollapse(true)} />}
      {collapse && (
        <div>
          {showTocMenu && (
            <div className="content-table-button" onClick={() => setCollapse(!collapse)}>
              <Tooltip placement={isHebrew ? 'left' : 'right'} title="Expand Table of Content">
                <TocIcon className={isHebrew ? 'content-table-icon' : ''} />
              </Tooltip>
            </div>
          )}

          {showDownloadButton && allowPreviewDownload && (
            <div className="content-table-button download-icon-header" onClick={downloadPdf}>
              <Tooltip placement={isHebrew ? 'left' : 'right'} title={configText('Download PDF')}>
                <Button
                  className={`medium-btn transparent-btn download-btn ${
                    downloadingPDF ? 'loading' : ''
                  }`}
                  icon={<DownloadIcon className="main-icon" />}>
                  {downloadingPDF && (
                    <>
                      <Spin indicator={<LoadingOutlined spin />} size="large" />
                      <CloseIcon className="close-icon" onClick={handleDownloadCancel} />
                    </>
                  )}
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      )}

      <div className="content-table-menu">
        <div className="content-table-header" onClick={() => setCollapse(!collapse)}>
          <span>{config?.['Table of Content'] || 'Table of Content'}</span>
          <TocIcon className={isHebrew ? 'content-table-icon' : ''} />
        </div>
        <Menu defaultSelectedKeys={['header']} mode="inline">
          {!isEditor &&
          (tocExclude.includes('header') || getIsHiddenFromProposal('header')) ? null : (
            <>
              {(!isHeaderHidden || isEditor) && (
                <Menu.Item
                  key={'header'}
                  className="editable"
                  onClick={() => handleScrollToSection({ sectionId: 'header' })}>
                  <span className="menu-item-text">{headlineName}</span>
                  <span className="menu-item-icons">
                    <TocActions
                      isEditor={isEditor}
                      tocExclude={tocExclude}
                      tocTextKey="header"
                      proposalKey="header"
                      isHiddenFromProposal={getIsHiddenFromProposal('header')}
                      onToggleToc={updateTOCExclude}
                      onToggleProposal={updateProposalExclude}
                      onEdit={(e) => {
                        e.stopPropagation();
                        toggleHeadlineEdit(true);
                      }}
                    />

                    {path === '/d/:pid' && (
                      <Popover
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onVisibleChange={() => toggleHeadlineEdit(!headlineVisible)}
                        trigger="click"
                        overlayClassName="edit-container-popover"
                        visible={headlineVisible}
                        content={
                          <div onClick={(e) => e.stopPropagation()}>
                            <Input
                              value={headlineName}
                              onChange={(e) => {
                                if (headlineNameError) setHeadlineNameError('');
                                setHeadlineName(
                                  !!sanitizeString(e.target.value).trim()
                                    ? sanitizeString(e.target.value)
                                    : ''
                                );
                              }}
                              onKeyPress={(e) => saveByEnter(e, 'update_headline', 'Headline')}
                            />
                            <p className="error-field">{headlineNameError}</p>

                            <div className="button-wrapper">
                              <Button
                                className="medium-btn secondary-btn"
                                onClick={() => {
                                  if (!headlineName) {
                                    return setHeadlineNameError('Headline cannot be empty');
                                  }
                                  updateTOCText({
                                    actionType: 'update_headline',
                                    value: headlineName,
                                    sectionId: 'Headline',
                                  });
                                  toggleHeadlineEdit(false);
                                }}>
                                Save
                              </Button>
                              <Button
                                className="medium-btn grey-btn"
                                onClick={() => {
                                  toggleHeadlineEdit(false);
                                  setHeadlineName(configHeadline);
                                  setHeadlineNameError('');
                                }}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        }>
                        <span className="toc-edit-anchor"></span>
                      </Popover>
                    )}
                  </span>
                  {getIsHiddenFromProposal('header') && (
                    <Tooltip title="Hidden from proposal">
                      <EyeoffIcon2
                        onClick={(e) =>
                          updateProposalExclude(e, 'header', !getIsHiddenFromProposal('header'))
                        }
                        className="toc-status-icon toc-status-icon-toc"
                      />
                    </Tooltip>
                  )}
                  {tocExclude.includes('header') && (
                    <Tooltip title="Hidden from table of content">
                      <TextCrossIcon
                        onClick={(e) =>
                          updateTOCExclude(e, 'header', !tocExclude.includes('header'))
                        }
                        className="toc-status-icon toc-status-icon-proposal"
                      />
                    </Tooltip>
                  )}
                </Menu.Item>
              )}
            </>
          )}

          {_.compact(
            _.map(sectionOrder, (s, sectionIndex) => {
              const sectionName = proposal?.draft?.[s]?.sectionName;

              const stitle =
                sectionName ||
                getSectionTitle({
                  proposal: proposal,
                  sectionName: s,
                  sectionIndex,
                });

              const isImage = fullWidthImgArray.includes(s);
              const text = stitle || s;

              // text2 -> based on this value (uuid or text) the toggle functionality will work
              const text2 = s.length === 36 && s.includes('-') ? s : text;

              // if not editor, if excluded from TOC don't show th text
              if (!isEditor && (tocExclude.includes(text2) || getIsHiddenFromProposal(s))) {
                return null;
              }

              return (
                <Menu.Item
                  key={sectionIndex}
                  onClick={() => handleScrollToSection({ sectionId: s })}>
                  <TocItem
                    {...{
                      proposal,
                      toggleFullImageEdit,
                      fullImageVisible,
                      fullImageName,
                      fullImageNameError,
                      setFullImageNameError,
                      getCurrentName,
                      setFullImageName,
                      saveByEnter,
                      updateTOCText,
                      isEditor,
                      isImage,
                      tocExclude,
                      updateTOCExclude,
                      updateProposalExclude,
                      isHiddenFromProposal: getIsHiddenFromProposal(s),
                      sectionId: s,
                      sectionIndex: sectionIndex,
                    }}
                  />
                </Menu.Item>
              );
            })
          )}

          {!isEditor &&
          (['signature', getStitle(proposal, 'signature', 0)].some((val) =>
            tocExclude.includes(val)
          ) ||
            getIsHiddenFromProposal('signature')) ? null : (
            <Menu.Item
              key={'signature'}
              onClick={() => handleScrollToSection({ sectionId: 'signature' })}>
              <TocItem
                {...{
                  proposal,
                  toggleFullImageEdit,
                  fullImageVisible,
                  fullImageName,
                  fullImageNameError,
                  setFullImageNameError,
                  getCurrentName,
                  setFullImageName,
                  saveByEnter,
                  updateTOCText,
                  isEditor,
                  tocExclude,
                  updateTOCExclude,
                  updateProposalExclude,
                  isHiddenFromProposal: getIsHiddenFromProposal('signature'),
                  sectionId: 'signature',
                  sectionIndex: 0,
                  isImage: false,
                }}
              />
            </Menu.Item>
          )}
        </Menu>
        {showDownloadButton && allowPreviewDownload && (
          <div className="content-table-footer">
            <div onClick={downloadPdf}>
              <DownloadIcon className="download-icon" />
              <span className="link-text">{configText('Download PDF')}</span>
            </div>
          </div>
        )}
      </div>
    </Layout.Sider>
  );
};

const getStitle = (proposal, sectionId, sectionIndex) => {
  const sectionName = proposal?.draft?.[sectionId]?.sectionName;

  const stitle =
    sectionName ||
    getSectionTitle({
      proposal: proposal,
      sectionName: sectionId,
      sectionIndex,
    });

  return stitle;
};

/**
 * The TOC item component.
 * @param {Object} proposal - The proposal object.
 * @param {string} sectionId - The section id.
 * @param {number} sectionIndex - The section index.
 * @param {function} toggleFullImageEdit - Function to toggle the full image edit.
 * @param {boolean} fullImageVisible - Whether the full image is visible.
 * @param {string} fullImageName - The full image name.
 * @param {string} fullImageNameError - The full image name error.
 * @param {function} setFullImageNameError - Function to set the full image name error.
 * @param {function} getCurrentName - Function to get the current name.
 * @param {function} setFullImageName - Function to set the full image name.
 * @param {function} saveByEnter - Function to save the text of a section by pressing enter.
 * @param {function} updateTOCText - Function to update the text of a section.
 * @param {boolean} isEditor - Whether the editor is open.
 * @param {boolean} isImage - Whether the section is an image.
 * @param {Array} tocExclude - The exclude list of the table of content.
 * @param {function} updateTOCExclude - Function to update the exclude list of the table of content.
 * @param {function} updateProposalExclude - Function to update the exclude list of the proposal.
 * @param {boolean} isHiddenFromProposal - Whether the section is hidden from the proposal.
 */
const TocItem = ({
  proposal,
  sectionId,
  sectionIndex,
  toggleFullImageEdit,
  fullImageVisible,
  fullImageName,
  fullImageNameError,
  setFullImageNameError,
  getCurrentName,
  setFullImageName,
  saveByEnter,
  updateTOCText,
  isEditor,
  isImage,
  tocExclude,
  updateTOCExclude,
  updateProposalExclude,
  isHiddenFromProposal,
}) => {
  const stitle = getStitle(proposal, sectionId, sectionIndex);
  const tocTextKey =
    sectionId.length === 36 && sectionId.includes('-') ? sectionId : stitle || sectionId;

  return (
    <>
      <span className="menu-item-text">{stitle}</span>
      <span className="menu-item-icons">
        <TocActions
          isEditor={isEditor}
          tocExclude={tocExclude}
          tocTextKey={tocTextKey}
          proposalKey={sectionId}
          isHiddenFromProposal={isHiddenFromProposal}
          onToggleToc={updateTOCExclude}
          onToggleProposal={updateProposalExclude}
          onEdit={(e) => {
            e.stopPropagation();
            setFullImageName(stitle || sectionId);
            toggleFullImageEdit(sectionId);
          }}
        />

        {isEditor && (
          <Popover
            onClick={(e) => {
              e.stopPropagation();
            }}
            onVisibleChange={(open) => toggleFullImageEdit(open ? sectionId : false)}
            trigger="click"
            overlayClassName="edit-container-popover"
            visible={fullImageVisible === sectionId}
            content={
              <div onClick={(e) => e.stopPropagation()}>
                <Input
                  value={fullImageName}
                  onChange={(e) => {
                    if (fullImageNameError) setFullImageNameError('');
                    getCurrentName(sectionId, e.target.value);
                    setFullImageName(
                      !!sanitizeString(e.target.value).trim() ? sanitizeString(e.target.value) : ''
                    );
                  }}
                  onKeyPress={(e) =>
                    saveByEnter(e, isImage ? 'update_fullimage' : 'update_sectionName', sectionId)
                  }
                />
                <p className="error-field">{fullImageNameError}</p>

                <div className="button-wrapper">
                  <Button
                    className="medium-btn secondary-btn"
                    onClick={() => {
                      if (!fullImageName) {
                        return setFullImageNameError('Name cannot be empty');
                      }
                      updateTOCText({
                        actionType: isImage ? 'update_fullimage' : 'update_sectionName',
                        value: fullImageName,
                        sectionId: sectionId,
                      });
                      toggleFullImageEdit(false);
                    }}>
                    Save
                  </Button>
                  <Button
                    className="medium-btn grey-btn"
                    onClick={() => {
                      toggleFullImageEdit(false);
                      setFullImageName(stitle || sectionId);
                      setFullImageNameError('');
                    }}>
                    Cancel
                  </Button>
                </div>
              </div>
            }>
            <span className="toc-edit-anchor"></span>
          </Popover>
        )}
      </span>
      {isHiddenFromProposal && (
        <Tooltip title="Hidden from proposal">
          <EyeoffIcon2
            onClick={(e) => updateProposalExclude(e, sectionId, !isHiddenFromProposal)}
            className="toc-status-icon toc-status-icon-proposal"
          />
        </Tooltip>
      )}
      {tocExclude.includes(tocTextKey) && (
        <Tooltip title="Hidden from table of content">
          <TextCrossIcon
            onClick={(e) => updateTOCExclude(e, tocTextKey, !tocExclude.includes(tocTextKey))}
            className="toc-status-icon toc-status-icon-toc"
          />
        </Tooltip>
      )}
    </>
  );
};

/**
 * The TOC actions component.
 * @param {boolean} isEditor - Whether the editor is open.
 * @param {Array} tocExclude - The exclude list of the table of content.
 * @param {string} tocTextKey - The text key of the table of content.
 * @param {string} proposalKey - The key of the proposal.
 * @param {boolean} isHiddenFromProposal - Whether the section is hidden from the proposal.
 * @param {function} onToggleToc - Function to toggle the exclude list of the table of content.
 * @param {function} onToggleProposal - Function to toggle the exclude list of the proposal.
 * @param {function} onEdit - Function to edit the section.
 * @returns {JSX.Element} - The TOC actions component.
 */
const TocActions = ({
  isEditor,
  tocExclude,
  tocTextKey,
  proposalKey,
  isHiddenFromProposal,
  onToggleToc,
  onToggleProposal,
  onEdit,
}) => {
  if (!isEditor) {
    return null;
  }

  const isHiddenFromToc = tocExclude.includes(tocTextKey);

  return (
    <Popover
      trigger="click"
      placement="rightTop"
      overlayClassName="toc-actions-popover"
      content={
        <div className="toc-actions-list" onClick={(e) => e.stopPropagation()}>
          <button className="toc-action-row" onClick={onEdit}>
            <EditIcon3 className="toc-action-icon" />
            <span>Edit title</span>
          </button>
          <button
            className="toc-action-row"
            onClick={(e) => onToggleToc(e, tocTextKey, !isHiddenFromToc)}>
            {isHiddenFromToc ? (
              <TextIcon2 className="toc-action-icon" />
            ) : (
              <TextCrossIcon className="toc-action-icon" />
            )}
            <span>
              {isHiddenFromToc ? 'Show in table of content' : 'Hide from table of content'}
            </span>
          </button>
          <button
            className="toc-action-row"
            onClick={(e) => onToggleProposal(e, proposalKey, !isHiddenFromProposal)}>
            {isHiddenFromProposal ? (
              <EyeIcon2 className="toc-action-icon" />
            ) : (
              <EyeoffIcon2 className="toc-action-icon" />
            )}
            <span>{isHiddenFromProposal ? 'Show in proposal' : 'Hide from proposal'}</span>
          </button>
        </div>
      }>
      <span className="toc-actions-control">
        <button className="toc-actions-trigger" type="button" aria-label="Section actions">
          <MoreIcon className="toc-action-more-icon" />
        </button>
      </span>
    </Popover>
  );
};

/**
 * The default props for the content table.
 * @returns {Object} - The default props.
 */
ContentTable.defaultProps = {
  collapse: false,
  configText: null,
  setCollapse: () => {},
  downloadPdf: () => {},
  contentTableClassName: '',
  clientWixPreview: false,
  templateUserWixPreview: false,
  showTocMenu: false,
  showDownloadButton: false,
};

/**
 * The prop types for the content table.
 * @returns {Object} - The prop types.
 */
ContentTable.propTypes = {
  proposal: PropTypes.instanceOf(Object).isRequired,
  user: PropTypes.instanceOf(Object),
  collapse: PropTypes.bool,
  configText: PropTypes.instanceOf(Object),
  setCollapse: PropTypes.func,
  downloadPdf: PropTypes.func,
  contentTableClassName: PropTypes.string,
  config: PropTypes.instanceOf(Object).isRequired,
  clientWixPreview: PropTypes.bool,
  templateUserWixPreview: PropTypes.bool,
  showTocMenu: PropTypes.bool,
  showDownloadButton: PropTypes.bool,
};

export default React.memo(ContentTable);
